import { useQueryClient } from '@tanstack/react-query';
import { memo, useEffect } from 'react';

import { type Board, type Clip } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';

import { GalleryView, type GalleryViewProps } from '~/components/v2/components/shared/gallery/GalleryView';
import { PublicShowcaseEmptySearchResultsState } from '~/components/v2/components/showcase/PublicShowcaseEmptySearchResultsState';
import { PublicShowcaseNullState } from '~/components/v2/components/showcase/PublicShowcaseNullState';
import { usePublicPermissionedVisibleCustomFieldsLength } from '~/components/v2/hooks/usePublicPermissionedVisibleCustomFieldsLength';
import { getPublicSearchTotalKey } from '~/constants/react-query-keys';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useGroupedGallerySelectedItems } from '~/hooks/useGroupedGallerySelectedItems';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';
import { type SearchTotals } from '~/types/SearchTotals';

export type PublicShowcaseGalleryViewProps = Pick<GalleryViewProps<Board, Clip>, 'className' | 'scrollElementRef'>;

export const PublicShowcaseGalleryView = memo(({ className, scrollElementRef }: PublicShowcaseGalleryViewProps) => {
  const { shortId } = useShortIdContext();
  const queryClient = useQueryClient();
  const { renderAsset, renderBoard, renderFile, useGalleryViewItems } = usePublicShowcaseContext();
  const itemsData = useGalleryViewItems({ shortId });
  const { isSearchActive } = useIsSearchActive();
  // This flat gallery is the SEARCH surface now — results span the whole showcase, so they cannot
  // be drawn by a page's blocks — and every surface reaches it, the Edit tab included. The surface's
  // card renderers therefore apply here as well as through the listing blocks.

  useEffect(() => {
    queryClient.setQueryData<SearchTotals>(getPublicSearchTotalKey(shortId), {
      total: itemsData.data.boards.total + itemsData.data.clips.total + itemsData.data.files.total,
    });
  }, [itemsData, queryClient, shortId]);

  return (
    <GalleryView
      className={className}
      itemsData={itemsData}
      scrollElementRef={scrollElementRef}
      NullState={isSearchActive ? PublicShowcaseEmptySearchResultsState : PublicShowcaseNullState}
      renderAsset={renderAsset}
      renderBoard={renderBoard}
      renderFile={renderFile}
      renderHeader={undefined}
      // The public permission model's custom-field count — every public gallery states it.
      useCustomFieldsLength={usePublicPermissionedVisibleCustomFieldsLength}
      // This surface mounts ONE gallery, so it registers its own items as the selectable set.
      useSelectableItems={useGroupedGallerySelectedItems}
    />
  );
});

PublicShowcaseGalleryView.displayName = 'PublicShowcaseGalleryView';
